const CM_PER_INCH = 2.54;

/**
 * Reads an inch value written as a whole number, decimal or mixed fraction
 * ("36", "25.5", "25 1/2", "7/8", "33”").
 * @returns {number|null}
 */
function parseInches(text) {
  const match = text
    .replace(/["”″]/g, "")
    .trim()
    .match(/^(?:(\d+(?:\.\d+)?)\s*)?(?:(\d+)\/(\d+))?$/);

  if (!match || (!match[1] && !match[2])) return null;

  const whole = match[1] ? parseFloat(match[1]) : 0;
  const fraction = match[2] ? Number(match[2]) / Number(match[3]) : 0;
  const inches = whole + fraction;

  return Number.isFinite(inches) ? inches : null;
}

const normalize = (text) => text.replace(/\s+/g, " ").trim().toLowerCase();

class SizeGuideTrigger extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    this.template = this.querySelector("template");

    this.button.addEventListener("click", () => {
      this.button.dispatchEvent(
        new CustomEvent("markup-modal:open", {
          bubbles: true,
          detail: { target: "size-guide-modal", html: this.template.innerHTML },
        }),
      );
    });
  }
}

/**
 * Drawer content for the Size & Fit Guide. Its size select stays in step with
 * the product form's, and the body measurements follow whichever size is
 * chosen, read from the size chart's column for that size. The in/cm toggle converts
 * both.
 */
class SizeGuide extends HTMLElement {
  connectedCallback() {
    this.unit = "in";
    this.productSelect = this.dataset.selectId
      ? document.getElementById(this.dataset.selectId)
      : null;
    this.select = this.querySelector("[data-size-guide-select]");
    this.chart = this.querySelector("[data-size-chart] table");
    this.heading = this.querySelector("[data-measurements-heading]");
    this.prompt = this.querySelector("[data-measurements-prompt]");
    this.list = this.querySelector("[data-measurements-list]");

    this.chart?.querySelectorAll("td, th").forEach((cell) => {
      cell.dataset.value = cell.textContent.trim();
    });

    if (this.select && this.productSelect) {
      this._copyOptions();
      this.select.addEventListener("change", this._onSelectChange);
    }

    this.addEventListener("change", this._onUnitChange);

    this._renderMeasurements();
  }

  get size() {
    return this.select?.value || "";
  }

  // Copied rather than rendered so sold-out labels match the product form's,
  // including ones it has updated since the page loaded. Its fit-note option
  // is left behind.
  _copyOptions() {
    const options = [...this.productSelect.options].filter(
      (option, index) => index === 0 || option.value,
    );

    this.select.replaceChildren(
      ...options.map((option) => option.cloneNode(true)),
    );
    this.select.value = this.productSelect.value;
  }

  _onSelectChange = () => {
    this.productSelect.value = this.select.value;
    this.productSelect.dispatchEvent(new Event("change", { bubbles: true }));
    this._renderMeasurements();
  };

  _onUnitChange = (e) => {
    if (!e.target.matches("[data-unit-input]")) return;

    this.unit = e.target.checked ? "cm" : "in";
    this._renderChart();
    this._renderMeasurements();
  };

  // Anything that doesn't read as inches is left as written — the merchant
  // opts into the toggle per guide.
  _toCm(text) {
    const inches = parseInches(text);
    if (inches === null) return text;

    return String(Math.round(inches * CM_PER_INCH * 10) / 10);
  }

  _value(cell) {
    return this.unit === "cm"
      ? this._toCm(cell.dataset.value)
      : cell.dataset.value;
  }

  _rows() {
    return this.chart ? [...this.chart.rows] : [];
  }

  _renderChart() {
    this._rows()
      .slice(1)
      .forEach((row) => {
        [...row.cells].slice(1).forEach((cell) => {
          cell.textContent = this._value(cell);
        });
      });
  }

  _renderMeasurements() {
    if (!this.heading) return;

    const [headerRow, ...rows] = this._rows();
    const type = this.dataset.measurementType;
    const unit = window.theme.translations(
      this.dataset,
      this.unit === "cm" ? "localeUnitCm" : "localeUnitIn",
    );
    const column = headerRow
      ? [...headerRow.cells].findIndex(
          (cell, index) =>
            index > 0 &&
            this.size &&
            normalize(cell.dataset.value) === normalize(this.size),
        )
      : -1;

    // No size chosen, or the chart has no column for it.
    if (column < 1) {
      this.heading.textContent = window.theme.translations(
        this.dataset,
        "localeHeadingUnsized",
        { type, unit },
      );
      if (this.prompt) this.prompt.hidden = false;
      this.list.hidden = true;
      return;
    }

    this.heading.textContent = window.theme.translations(
      this.dataset,
      "localeHeading",
      { type, size: this.size, unit },
    );

    const items = rows
      .filter((row) => row.cells[column])
      .map((row) => {
        const item = document.createElement("div");
        const term = document.createElement("dt");
        const value = document.createElement("dd");
        const symbol = document.createElement("span");

        item.className = "size-guide__measurement";
        term.className = "size-guide__measurement-label eyebrow";
        value.className = "size-guide__measurement-value body-large";
        term.textContent = row.cells[0].dataset.value;
        value.textContent = this._value(row.cells[column]);
        symbol.textContent = this.unit === "cm" ? " cm" : "”";
        symbol.setAttribute("aria-hidden", "true");
        value.append(symbol);
        item.append(term, value);

        return item;
      });

    this.list.replaceChildren(...items);
    this.list.hidden = false;
    if (this.prompt) this.prompt.hidden = true;
  }
}

if (!customElements.get("size-guide-trigger")) {
  customElements.define("size-guide-trigger", SizeGuideTrigger);
}

if (!customElements.get("size-guide")) {
  customElements.define("size-guide", SizeGuide);
}
